import { logger } from "../logger";
import SkybusProviderBase from "../provider/base";
import { ChannelDefinition } from "../transport/types";
import { WireMsg, WireType } from "../types";
import UnifiedSkybus, { SkybusPromise, SUBSCRIPTION_RETRY_DEFAULT_INTERVAL } from "./index";
import { Subscription } from "./subscription";

/**
 * Per-channel configuration for the optimistic update behavior.
 *
 * For a given request/response channel pair, this describes how to
 * synthesize an optimistic response, how to re-apply pending changes when a
 * vehicle-initiated update arrives mid-flight, and (optionally) how to
 * coalesce queued requests.
 */
export interface OptimisticChannelConfig<
  TRequest extends WireMsg & { setUtime(utime: number): void },
  TResponse extends WireMsg,
> {
  requestChannel: ChannelDefinition<WireType<TRequest>>;
  responseChannel: ChannelDefinition<WireType<TResponse>>;

  /**
   * Build the synthetic response that should be broadcast to listeners
   * immediately when a mutation is kicked off. `current` is the view most
   * recently broadcast on the response channel (real or optimistic) — it may
   * be `undefined` if no message has arrived yet.
   */
  buildOptimisticResponse: (current: TResponse | undefined, request: TRequest) => TResponse;

  /**
   * Called when a vehicle-initiated (non-matching) message arrives on the
   * response channel while a mutation is still in flight. Re-apply the
   * pending request's delta on top of the new base so the optimistic view
   * is preserved.
   */
  rebase: (newBase: TResponse, pendingRequest: TRequest) => TResponse;

  /**
   * Optional: merge a queued request with a newer one. Return `null` if the
   * two cannot be safely coalesced (e.g. non-commutative operations). If
   * omitted, queued requests are sent one-by-one in FIFO order.
   *
   * COALESCING CONTRACT — important for callers:
   * When two requests are coalesced, BOTH callers' `SkybusPromise`s
   * resolve with the SAME response — namely the ack of the *merged*
   * request, which carries the *incoming* request's nonce (not the
   * earlier caller's). Code that needs per-call rejection identification
   * must therefore check `latestRequestRejectionNonce !== 0` (or any
   * other "is rejected" predicate) on the resolved response, NOT
   * compare it to its own request's nonce. The coalesced response
   * authoritatively answers both callers.
   */
  coalesce?: (queued: TRequest, incoming: TRequest) => TRequest | null;

  /**
   * Optional: classify a successful (matching) response as a vehicle-side
   * rejection. When provided and it returns true, the inflight is still
   * settled with the response (the caller's promise resolves and they can
   * introspect rejection fields), but the layer ALSO:
   *   - fires `onOptimisticError` with `reason = response`,
   *   - consults `onInflightFailure` to decide whether to abort queued
   *     entries (with `err = response`),
   *   - emits the `optimistic_skybus.request_rejected` analytics event.
   *
   * Without this predicate, a vehicle rejection looks identical to a
   * successful ack to the layer (both pass `matchFn`); only the call
   * site can tell them apart by inspecting fields on the response.
   */
  isRejection?: (response: TResponse, request: TRequest) => boolean;

  /**
   * Optional policy for what happens to queued entries when the inflight
   * one fails — i.e. (a) timeout / activation failure, or (b) a vehicle
   * rejection classified by `isRejection`.
   *
   *   "drain" (default): keep queued entries; `drainQueue` promotes the
   *                      next one. The optimistic view is rolled back to
   *                      `realLatest` plus remaining queued overlays.
   *   "abort":           reject every queued entry with the same error,
   *                      clear the queue, fire `onOptimisticError` for
   *                      each, and emit `queue_aborted`. The optimistic
   *                      view rolls back to `realLatest`.
   *   function:          per-entry decision; return "abort" or "drain".
   *                      Useful when you want to abort only on certain
   *                      failure kinds (e.g. abort on transport errors,
   *                      drain on rejections).
   */
  onInflightFailure?: "drain" | "abort" | ((queued: TRequest, err: unknown) => "abort" | "drain");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyOptimisticConfig = OptimisticChannelConfig<any, any>;

/** Extra config field accepted by `makeRequest` to opt in to the optimistic path. */
export interface OptimisticRequestConfig {
  timeout?: number;
  retries?: number;
  debugKey?: string;
  /**
   * Opt in to optimistic handling. If true, the request/response channel
   * pair MUST be registered in the subclass's configs — otherwise
   * `makeRequest` throws. Defaults to false (transparent pass-through to
   * UnifiedSkybus).
   */
  optimistic?: boolean;
}

export interface OptimisticErrorEvent<TRequest extends WireMsg> {
  request: TRequest;
  reason: unknown;
}

/**
 * Analytics logger shape. Wire this to datadog (or
 * equivalent) at the application layer so we can debug customer issues
 */
export type OptimisticAnalyticsLogger = (
  eventName: OptimisticSkybusEvent,
  context?: Record<string, unknown>
) => void;

/**
 * Stable set of analytics events emitted by `OptimisticUnifiedSkybus`.
 * Adding or renaming an event is a logging-contract change — please update
 * any Datadog dashboards or monitors that reference these names.
 */
export type OptimisticSkybusEvent =
  | "optimistic_skybus.constructed"
  | "optimistic_skybus.connection_changed"
  | "optimistic_skybus.subscription_activation_started"
  | "optimistic_skybus.subscription_activation_succeeded"
  | "optimistic_skybus.subscription_activation_failed"
  | "optimistic_skybus.subscription_reactivation_scheduled"
  | "optimistic_skybus.request_enqueued"
  | "optimistic_skybus.request_coalesced"
  | "optimistic_skybus.request_published"
  | "optimistic_skybus.request_retried"
  | "optimistic_skybus.request_acked"
  | "optimistic_skybus.request_rejected"
  | "optimistic_skybus.request_rebased_on_vehicle_update"
  | "optimistic_skybus.request_failed"
  | "optimistic_skybus.request_stopped"
  | "optimistic_skybus.gates_stuck"
  | "optimistic_skybus.queue_drained"
  | "optimistic_skybus.queue_aborted"
  | "optimistic_skybus.user_callback_threw";

/**
 * Match function used by `makeRequest` to decide whether a given response
 * answers a specific request.
 *
 * IMPORTANT — the optimistic layer broadcasts synthetic responses through
 * the same subscription that vehicle messages flow through. The interceptor
 * tags self-broadcasts and skips them, but other listeners on the same
 * channel (including the base `UnifiedSkybus.makeRequest` listener used by
 * non-optimistic callers) cannot distinguish synthetic from real. Match
 * functions MUST therefore be nonce-scoped (e.g. equal `latestRequestNonce`
 * to the request's `nonce`) so that:
 *   1. a synthetic response built for caller A is not mistaken for an ack
 *      of caller B's concurrent non-optimistic request on the same channel,
 *   2. two callers' nonces never collide.
 *
 * A match function that ignores the nonce is undefined behavior in the
 * optimistic context.
 */
type MatchFn<TResponse, TRequest> = (latest: TResponse, requested: TRequest) => boolean;

/**
 * Activation result: `null` means the handshake succeeded, an `Error` means
 * it failed. Using a sentinel rather than a rejected promise makes it
 * impossible for activation failures to surface as unhandled promise
 * rejections when no optimistic request has chained off them yet.
 */
type ActivationResult = null | Error;

interface PendingEntry<
  TRequest extends WireMsg & { setUtime(utime: number): void },
  TResponse extends WireMsg,
> {
  request: TRequest;
  matchFn: MatchFn<TResponse, TRequest>;
  resolve: (resp: TResponse) => void;
  reject: (err: unknown) => void;
  requestConfig: { timeout?: number; retries?: number; debugKey?: string };
  /** Retry timer for the in-flight entry (set when it starts). */
  retryTimer?: ReturnType<typeof setInterval>;
  /**
   * Watchdog timer scheduled in `startInflight` to log `gates_stuck` if
   * neither gate (connected / activation) resolves promptly. Stored on the
   * entry so `cancelEntry` and `failInflight` can clear it; otherwise a
   * stopped/failed entry could fire a misleading diagnostic event seconds
   * after the entry is gone.
   */
  watchdog?: ReturnType<typeof setTimeout>;
  /**
   * Set when `SkybusPromise.stop()` is called by the caller. The class
   * uses this to bail out at the earliest safe point (gates, retry timer,
   * queue drain) without rejecting the promise — matching the contract of
   * the base `SkybusPromise.stop()` (the promise simply never settles).
   */
  stopped: boolean;
}

interface ChannelState<
  TRequest extends WireMsg & { setUtime(utime: number): void },
  TResponse extends WireMsg,
> {
  config: OptimisticChannelConfig<TRequest, TResponse>;
  subscription: Subscription<TResponse>;
  /**
   * Resolves (never rejects — see `ActivationResult`) once the provider has
   * acked the response-channel subscription. Awaited before we publish any
   * optimistic request so we don't fire blind and eat the retry timeout
   * waiting for a response that can't be delivered yet.
   *
   * Replaced by `tryActivate` whenever a previous activation failed and the
   * transport reports reconnected. Existing requests waiting at the gate
   * pick up the new activation when they next read `state.activation`.
   */
  activation: Promise<ActivationResult>;
  /** Most recent activation outcome, used to decide whether to re-attempt. */
  activationResult: "pending" | "succeeded" | "failed";
  /** Most recent message known to have come from the vehicle. */
  realLatest: TResponse | undefined;
  /** Most recent message broadcast to listeners (real or synthetic). */
  currentView: TResponse | undefined;
  inflight: PendingEntry<TRequest, TResponse> | null;
  queue: Array<PendingEntry<TRequest, TResponse>>;
  /** Reentrancy guard: true while the class itself is invoking pushNewLatest. */
  isPushingSynthetic: boolean;
  errorListeners: Array<(event: OptimisticErrorEvent<TRequest>) => void>;
}

function channelKey(
  req: ChannelDefinition<WireType<WireMsg>>,
  resp: ChannelDefinition<WireType<WireMsg>>
): string {
  return `${req.channel}->${resp.channel}`;
}

/**
 * Best-effort extract of the analytics-friendly fields we want to send to
 * Datadog for every event. Pulls `nonce` / `latestRequestNonce` /
 * `latestRequestRejectionNonce` / `utime` if the message exposes them.
 * Always returns a plain object (empty if nothing was found) so log sites
 * can spread it without checks. Never throws.
 */
function extractAnalyticsContext(msg: unknown): Record<string, unknown> {
  if (msg == null) return {};
  const out: Record<string, unknown> = {};
  const anyMsg = msg as {
    getNonce?: () => unknown;
    getLatestRequestNonce?: () => unknown;
    getUtime?: () => unknown;
    getLatestRequestRejectionNonce?: () => unknown;
  };
  try {
    if (typeof anyMsg.getNonce === "function") out.nonce = anyMsg.getNonce();
  } catch {
    /* skip */
  }
  try {
    if (typeof anyMsg.getLatestRequestNonce === "function") {
      out.latestRequestNonce = anyMsg.getLatestRequestNonce();
    }
  } catch {
    /* skip */
  }
  try {
    if (typeof anyMsg.getLatestRequestRejectionNonce === "function") {
      out.latestRequestRejectionNonce = anyMsg.getLatestRequestRejectionNonce();
    }
  } catch {
    /* skip */
  }
  try {
    if (typeof anyMsg.getUtime === "function") out.utime = anyMsg.getUtime();
  } catch {
    /* skip */
  }
  return out;
}

/**
 * A `UnifiedSkybus` subclass that adds opt-in optimistic handling for a
 * statically configured set of (request, response) channel pairs.
 *
 * Callers use the same `makeRequest(...)` API as before. Passing
 * `{ optimistic: true }` in the config opts the call in to the optimistic
 * path: a synthetic response is broadcast to subscribers immediately, the
 * real request is enqueued behind any in-flight one, and vehicle-initiated
 * updates received mid-flight are rebased on top of the pending delta.
 *
 * If `{ optimistic: true }` is passed for a channel pair that has not been
 * registered via `OPTIMISTIC_CHANNEL_CONFIGS`, `makeRequest` throws a clear
 * error at the call site rather than silently falling back.
 */
export default class OptimisticUnifiedSkybus extends UnifiedSkybus {
  private readonly optimisticConfigs: Map<string, AnyOptimisticConfig>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly channelStates: Map<string, ChannelState<any, any>>;
  private readonly analyticsLogger: OptimisticAnalyticsLogger | undefined;

  /**
   * Gate that resolves when the transport is connected. On disconnect we
   * replace the gate with a new pending promise; on reconnect we resolve
   * the latest gate. New optimistic requests wait on this gate before
   * publishing so we never publish into a disconnected transport (which
   * would silently eat the entire retry timeout waiting for a response
   * that can't round-trip).
   *
   * NOTE: `startInflight` re-samples `this.provider.isConnected()` at
   * publish time and short-circuits the gate if the transport already
   * reports connected. This protects us from transports that mutate
   * their connected flag without firing the listeners this gate depends
   * on (the `LivekitTransportLayer` historically did exactly that).
   */
  private connectedGate: Promise<void>;
  /**
   * Initialized to a no-op so it is always callable, even when the gate
   * starts already-resolved. `makeConnectedGate` reassigns this when it
   * creates a new pending gate.
   */
  private resolveConnectedGate: () => void = () => {};

  constructor(
    provider: SkybusProviderBase,
    configs: ReadonlyArray<AnyOptimisticConfig>,
    analyticsLogger?: OptimisticAnalyticsLogger
  ) {
    super(provider);
    this.optimisticConfigs = new Map();
    this.channelStates = new Map();
    this.analyticsLogger = analyticsLogger;

    // Initialize the connection gate from the provider's current state.
    const initiallyConnected = provider.isConnected();
    this.connectedGate = this.makeConnectedGate(initiallyConnected);

    provider.addConnectionStatusListener(connected => {
      this.analytics("optimistic_skybus.connection_changed", { connected });
      if (connected) {
        this.resolveConnectedGate();
        // After reconnect, retry activation for any subscription whose
        // initial handshake failed. Successful activations remain valid;
        // the LivekitSkybusProvider's `republishExistingSubscriptions`
        // re-establishes the underlying provider-level subscription.
        for (const state of this.channelStates.values()) {
          if (state.activationResult === "failed") {
            this.scheduleReactivation(state);
          }
        }
      } else {
        // Install a fresh pending gate. Next optimistic request will wait
        // here until the transport reports connected again.
        this.connectedGate = this.makeConnectedGate(false);
      }
    });

    for (const cfg of configs) {
      const key = channelKey(cfg.requestChannel, cfg.responseChannel);
      if (this.optimisticConfigs.has(key)) {
        throw new Error(
          `[OptimisticUnifiedSkybus] Duplicate OptimisticChannelConfig for "${cfg.requestChannel.channel}" -> "${cfg.responseChannel.channel}".`
        );
      }
      this.optimisticConfigs.set(key, cfg);
      // Eagerly set up per-channel state so we start tracking vehicle
      // messages immediately, before any optimistic request is made.
      this.getOrCreateState(key, cfg);
    }

    this.analytics("optimistic_skybus.constructed", {
      channelCount: configs.length,
      channels: configs.map(c => channelKey(c.requestChannel, c.responseChannel)),
      initiallyConnected,
    });
  }

  public override makeRequest<
    TResponse extends WireMsg,
    TRequest extends WireMsg & { setUtime(utime: number): void },
  >(
    requestChannel: ChannelDefinition<WireType<TRequest>>,
    responseChannel: ChannelDefinition<WireType<TResponse>>,
    request: TRequest,
    matchRequestToResponse: MatchFn<TResponse, TRequest>,
    config?: OptimisticRequestConfig
  ): SkybusPromise<TRequest, TResponse> {
    if (!config?.optimistic) {
      return super.makeRequest(
        requestChannel,
        responseChannel,
        request,
        matchRequestToResponse,
        config
      );
    }
    const key = channelKey(requestChannel, responseChannel);
    const cfg = this.optimisticConfigs.get(key);
    if (!cfg) {
      // Synchronous throw on purpose: using `{ optimistic: true }` on an
      // unregistered channel is a developer mistake we want surfaced loudly
      // during development, not hidden inside a rejected promise one tick
      // later.
      throw new Error(
        `[OptimisticUnifiedSkybus] No OptimisticChannelConfig registered for ` +
          `"${requestChannel.channel}" -> "${responseChannel.channel}". ` +
          `Add it to OPTIMISTIC_CHANNEL_CONFIGS or drop { optimistic: true }.`
      );
    }
    return this.enqueueOptimistic(cfg, request, matchRequestToResponse, config);
  }

  /**
   * Subscribe to rejection/timeout events for a registered channel pair.
   * Fires when the class's own retry/timeout loop gives up on a request
   * (or activation of the response subscription fails). The event carries
   * the request that was in flight so callers can surface it in the UI.
   *
   * Listener callbacks are invoked inside a try/catch — a throwing listener
   * will not stop other listeners from firing and will not wedge the queue.
   */
  public onOptimisticError<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    requestChannel: ChannelDefinition<WireType<TRequest>>,
    responseChannel: ChannelDefinition<WireType<TResponse>>,
    callback: (event: OptimisticErrorEvent<TRequest>) => void
  ): () => void {
    const key = channelKey(requestChannel, responseChannel);
    const cfg = this.optimisticConfigs.get(key);
    if (!cfg) {
      throw new Error(
        `[OptimisticUnifiedSkybus] No OptimisticChannelConfig registered for ` +
          `"${requestChannel.channel}" -> "${responseChannel.channel}".`
      );
    }
    const state = this.getOrCreateState(key, cfg);
    state.errorListeners.push(callback);
    return () => {
      const idx = state.errorListeners.indexOf(callback);
      if (idx >= 0) state.errorListeners.splice(idx, 1);
    };
  }

  /** Invoke the analytics logger if one was provided; never throws. */
  private analytics(event: OptimisticSkybusEvent, context?: Record<string, unknown>): void {
    if (!this.analyticsLogger) return;
    try {
      this.analyticsLogger(event, context);
    } catch (err) {
      // A misbehaving logger must NEVER abort the optimistic flow.
      logger.error(`[OptimisticUnifiedSkybus] analytics logger threw on ${event}`, err);
    }
  }

  private makeConnectedGate(startConnected: boolean): Promise<void> {
    if (startConnected) {
      // Reassign to a no-op so the listener can call it unconditionally on
      // a duplicate `connected: true` event without throwing.
      this.resolveConnectedGate = () => {};
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      this.resolveConnectedGate = resolve;
    });
  }

  private enqueueOptimistic<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    cfg: OptimisticChannelConfig<TRequest, TResponse>,
    request: TRequest,
    matchFn: MatchFn<TResponse, TRequest>,
    config: OptimisticRequestConfig
  ): SkybusPromise<TRequest, TResponse> {
    const key = channelKey(cfg.requestChannel, cfg.responseChannel);
    const state = this.getOrCreateState(key, cfg);

    // Hoisted so the SkybusPromise's stop handler (defined below) can find
    // the entry it's responsible for cancelling.
    let entry: PendingEntry<TRequest, TResponse> | undefined;

    return new SkybusPromise<TRequest, TResponse>(
      (resolve, reject) => {
        entry = {
          request,
          matchFn,
          resolve,
          reject,
          requestConfig: {
            timeout: config.timeout,
            retries: config.retries,
            debugKey: config.debugKey,
          },
          stopped: false,
        };
        const enqueueContext = {
          channel: key,
          debugKey: config.debugKey,
          hasInflight: !!state.inflight,
          queueLen: state.queue.length,
          ...extractAnalyticsContext(request),
        };
        this.analytics("optimistic_skybus.request_enqueued", enqueueContext);

        if (state.inflight) {
          if (cfg.coalesce && state.queue.length > 0) {
            const tail = state.queue[state.queue.length - 1];
            const merged = cfg.coalesce(tail.request, request);
            if (merged !== null) {
              const previousResolve = tail.resolve;
              const previousReject = tail.reject;
              tail.request = merged;
              tail.matchFn = matchFn;
              tail.resolve = resp => {
                previousResolve(resp);
                resolve(resp);
              };
              tail.reject = err => {
                previousReject(err);
                reject(err);
              };
              this.analytics("optimistic_skybus.request_coalesced", {
                channel: key,
                debugKey: config.debugKey,
                queueLen: state.queue.length,
                ...extractAnalyticsContext(merged),
              });
              // NOTE: we push the original `request` (not `merged`) on
              // top of `currentView`. This is correct because
              // `currentView` already reflects the previously-queued
              // tail's overlay (it was pushed when the tail was first
              // enqueued). Passing `request` therefore layers ONLY the
              // new caller's delta on top, which combined with the
              // existing currentView equals what `merged` represents.
              this.pushOptimistic(state, request);
              return;
            }
          }
          state.queue.push(entry);
          this.pushOptimistic(state, request);
          return;
        }
        this.pushOptimistic(state, request);
        this.startInflight(state, entry);
      },
      cfg.requestChannel,
      cfg.responseChannel,
      // SkybusPromise.stop() handler. See note on PendingEntry.stopped.
      () => {
        if (!entry) return;
        this.cancelEntry(state, entry);
      }
    );
  }

  /**
   * Cancel a pending optimistic entry without rejecting its promise. This
   * matches the contract of the base `SkybusPromise.stop()` — retries and
   * the inflight slot are cleared but the promise simply never settles.
   * The caller is responsible for unmounting any UI that was waiting on it.
   */
  private cancelEntry<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>, entry: PendingEntry<TRequest, TResponse>) {
    if (entry.stopped) return;
    entry.stopped = true;

    this.analytics("optimistic_skybus.request_stopped", {
      channel: channelKey(state.config.requestChannel, state.config.responseChannel),
      debugKey: entry.requestConfig.debugKey,
      ...extractAnalyticsContext(entry.request),
    });

    // Clear retry + watchdog timers so a stopped entry doesn't fire a
    // misleading `gates_stuck` event seconds later.
    this.cancelTimers(entry);
    if (state.inflight === entry) {
      state.inflight = null;
      this.drainQueue(state);
      return;
    }
    const queueIdx = state.queue.indexOf(entry);
    if (queueIdx >= 0) state.queue.splice(queueIdx, 1);
  }

  /** Clears the retry interval and watchdog timeout on a pending entry. */
  private cancelTimers<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(entry: PendingEntry<TRequest, TResponse>) {
    if (entry.retryTimer) {
      clearInterval(entry.retryTimer);
      entry.retryTimer = undefined;
    }
    if (entry.watchdog) {
      clearTimeout(entry.watchdog);
      entry.watchdog = undefined;
    }
  }

  /**
   * Apply the channel's `onInflightFailure` policy to the queue. Splits
   * `state.queue` in place so it contains only survivors, and returns the
   * aborted entries — the caller is responsible for rejecting them and
   * firing error listeners after the rolled-back view has been broadcast.
   *
   * Returning the aborted entries (rather than rejecting them inline) lets
   * both call sites — `failInflight` and the rejection branch in the
   * interceptor — choose the right ordering between view broadcast and
   * promise rejection.
   */
  private applyFailurePolicy<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    state: ChannelState<TRequest, TResponse>,
    err: unknown
  ): Array<PendingEntry<TRequest, TResponse>> {
    const policy = state.config.onInflightFailure ?? "drain";
    if (policy === "drain") return [];
    const survivors: Array<PendingEntry<TRequest, TResponse>> = [];
    const aborted: Array<PendingEntry<TRequest, TResponse>> = [];
    for (const q of state.queue) {
      let decision: "abort" | "drain";
      if (policy === "abort") {
        decision = "abort";
      } else {
        try {
          decision = policy(q.request, err);
        } catch (policyErr) {
          // A throwing policy must not poison the queue. Default to drain
          // (the safer choice — we keep the entry rather than dropping it
          // silently) and log.
          logger.error(
            "[OptimisticUnifiedSkybus] onInflightFailure policy threw; defaulting to 'drain'",
            policyErr
          );
          this.analytics("optimistic_skybus.user_callback_threw", {
            callback: "onInflightFailure",
            error: policyErr instanceof Error ? policyErr.message : String(policyErr),
          });
          decision = "drain";
        }
      }
      if (decision === "abort") {
        aborted.push(q);
      } else {
        survivors.push(q);
      }
    }
    state.queue = survivors;
    if (aborted.length > 0) {
      this.analytics("optimistic_skybus.queue_aborted", {
        channel: channelKey(state.config.requestChannel, state.config.responseChannel),
        count: aborted.length,
        survivors: survivors.length,
        reason: err instanceof Error ? err.message : String(err),
      });
    }
    return aborted;
  }

  /**
   * Snapshot the error listeners before iterating — a listener can call
   * its returned unsubscribe function during the callback, which mutates
   * `state.errorListeners` mid-iteration. Iterating a snapshot guarantees
   * every listener registered at fire time gets a chance to run.
   */
  private fireErrorListeners<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>, event: OptimisticErrorEvent<TRequest>) {
    const listeners = [...state.errorListeners];
    for (const cb of listeners) {
      try {
        cb(event);
      } catch (listenerErr) {
        logger.error("[OptimisticUnifiedSkybus] onOptimisticError listener threw", listenerErr);
        this.analytics("optimistic_skybus.user_callback_threw", {
          callback: "onOptimisticError",
          error: listenerErr instanceof Error ? listenerErr.message : String(listenerErr),
        });
      }
    }
  }

  private getOrCreateState<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    key: string,
    cfg: OptimisticChannelConfig<TRequest, TResponse>
  ): ChannelState<TRequest, TResponse> {
    const existing = this.channelStates.get(key);
    if (existing) return existing as ChannelState<TRequest, TResponse>;

    const subscription = this.subscribe(cfg.responseChannel);
    // Capture the raw `pushNewLatest` before we overwrite it with the
    // interceptor; the interceptor uses this to pass messages through to
    // downstream listeners without re-entering itself.
    const originalPushNewLatest = subscription.pushNewLatest;

    const state: ChannelState<TRequest, TResponse> = {
      config: cfg,
      subscription,
      activation: Promise.resolve(null),
      activationResult: "pending",
      realLatest: undefined,
      currentView: undefined,
      inflight: null,
      queue: [],
      isPushingSynthetic: false,
      errorListeners: [],
    };

    // Intercept the subscription's `pushNewLatest` so we sit between the
    // transport and downstream subscribers. This lets us:
    //   - Replace a vehicle-initiated update with a rebased synthetic when a
    //     mutation is in flight, so downstream listeners never flicker
    //     through the raw vehicle state.
    //   - Let messages we ourselves broadcast (identified by
    //     `isPushingSynthetic`) flow straight through to downstream
    //     listeners without re-entering the interception logic.
    const interceptor = (msg: TResponse) => {
      if (state.isPushingSynthetic) {
        originalPushNewLatest(msg);
        return;
      }
      // Real vehicle message path.
      state.realLatest = msg;
      const inflight = state.inflight;
      const matchesInflight = inflight ? this.safeMatch(inflight, msg) : false;

      if (inflight && !matchesInflight) {
        // Vehicle-initiated update arrived while a mutation is in flight
        // and does NOT match our pending request. Rebase the in-flight
        // request (plus any queued ones) on top so the optimistic view
        // survives the intervening update, and broadcast the rebased view
        // INSTEAD of the raw vehicle msg.
        this.analytics("optimistic_skybus.request_rebased_on_vehicle_update", {
          channel: channelKey(cfg.requestChannel, cfg.responseChannel),
          queueLen: state.queue.length,
          msg: extractAnalyticsContext(msg),
          inflightRequest: extractAnalyticsContext(inflight.request),
        });
        const view = this.rebaseOntoBase(state, msg, inflight, state.queue);
        state.currentView = view;
        this.broadcastSynthetic(state, view);
        return;
      }

      if (inflight && matchesInflight) {
        // Matching response for the in-flight request. Before settling we
        // need to re-project the queued overlays on top of the new real
        // state — otherwise listeners would briefly see `msg` without the
        // queued optimistic deltas, then jump back when the next request
        // resolves. `settleInflight` resolves the in-flight promise with
        // the raw `msg` so callers still see the authoritative response.
        //
        // NOTE on overlapping rejection fields: if `msg` carries a
        // rejection for a key that is ALSO targeted by a queued overlay,
        // the rebased synthetic broadcast here will overwrite the
        // rejection state for that key with the queued caller's intent.
        // This is intentional: there is a fresh request pending for the
        // same key, so the prior rejection is not the current state of
        // intent for that key. The original (now-rejected) caller still
        // receives the raw `msg` via `settleInflight` and can detect the
        // rejection from its resolved promise. To convert a vehicle
        // rejection into queue-level cancellation, register `isRejection`
        // on the channel config (with `onInflightFailure: "abort"`).
        const isReject = cfg.isRejection?.(msg, inflight.request) ?? false;
        this.analytics(
          isReject ? "optimistic_skybus.request_rejected" : "optimistic_skybus.request_acked",
          {
            channel: channelKey(cfg.requestChannel, cfg.responseChannel),
            debugKey: inflight.requestConfig.debugKey,
            queueLen: state.queue.length,
            response: extractAnalyticsContext(msg),
            inflightRequest: extractAnalyticsContext(inflight.request),
          }
        );
        // Split the queue BEFORE rebasing, so the broadcast view doesn't
        // include overlays we're about to drop. `aborted` is empty when
        // this isn't a rejection or when the policy says "drain".
        const aborted = isReject ? this.applyFailurePolicy(state, msg) : [];
        if (state.queue.length > 0) {
          const view = this.rebaseOntoBase(state, msg, null, state.queue);
          state.currentView = view;
          this.broadcastSynthetic(state, view);
        } else {
          state.currentView = msg;
          originalPushNewLatest(msg);
        }
        this.settleInflight(state, inflight, msg);
        if (isReject) {
          // Fire error listeners for the rejected inflight. The caller
          // already has the response via the resolved promise; this is
          // for passive subscribers (e.g. a global toast manager) that
          // want to react to ANY failure on the channel.
          this.fireErrorListeners(state, { request: inflight.request, reason: msg });
          // Reject any queued entries the policy aborted.
          for (const q of aborted) {
            this.cancelTimers(q);
            q.reject(msg);
            this.fireErrorListeners(state, { request: q.request, reason: msg });
          }
        }
        return;
      }

      // No inflight request: pass the vehicle msg straight through.
      state.currentView = msg;
      originalPushNewLatest(msg);
    };

    // INVARIANT: `Subscription.pushNewLatest` must be an instance-bound
    // property (an arrow function stored on `this`), NOT a prototype method.
    // `Subscription.activate()` and `subscribeCheck()` both pass
    // `this.pushNewLatest` to the provider by property access, so replacing
    // the instance property rewires every future provider callback to our
    // interceptor. If that ever becomes a prototype method, this reassignment
    // will silently no-op and vehicle messages will bypass this class.
    // See the "interception wiring" tests in optimisticUnifiedSkybus.test.ts
    // which pin this contract.
    //
    // Runtime assertion: if Subscription is ever refactored to expose
    // `pushNewLatest` only on the prototype, fail loudly here in dev so
    // the bypass is caught at construction rather than as silent missing
    // optimistic updates at runtime.
    if (!Object.prototype.hasOwnProperty.call(subscription, "pushNewLatest")) {
      throw new Error(
        `[OptimisticUnifiedSkybus] Subscription.pushNewLatest is not an own ` +
          `property on the subscription instance for ` +
          `"${cfg.responseChannel.channel}". The optimistic interceptor ` +
          `relies on this invariant; see the comment above this assertion.`
      );
    }
    (subscription as unknown as { pushNewLatest: (msg: TResponse) => void }).pushNewLatest =
      interceptor;

    this.channelStates.set(key, state);

    // ORDER MATTERS: fire `activate()` BEFORE registering a listener.
    // `Subscription.activate()` early-returns if the subscription has
    // already been flipped to subscribed=true (e.g. by `listenLatest` ->
    // `subscribeCheck` -> `provider.subscribe`), in which case the async
    // `genSubscription` handshake never runs and we lose both the ability
    // to observe handshake failures and the guarantee that the vehicle
    // has acked the subscription before we publish. Activating first lets
    // `genSubscription` run; the no-op `listenLatest` below then just
    // bumps the reference count without re-subscribing.
    this.kickoffActivation(state);
    // The no-op listener keeps the subscription reference count > 0 so the
    // provider doesn't drop it on transient user unsubscribes.
    subscription.listenLatest(() => {
      /* no-op: this listener exists only to keep the subscription alive. */
    });

    return state;
  }

  /**
   * Run `subscription.activate()` and store the resulting promise (as an
   * `ActivationResult` sentinel) on `state.activation`. Always resolves —
   * a rejected activate() is converted to an `Error` sentinel so we can
   * surface it to callers without leaving an unhandled rejection on the
   * floor when nothing has chained off `activation` yet.
   */
  private kickoffActivation<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>): void {
    state.activationResult = "pending";
    const channel = state.config.responseChannel.channel;
    this.analytics("optimistic_skybus.subscription_activation_started", { channel });
    state.activation = state.subscription.activate().then(
      () => {
        state.activationResult = "succeeded";
        this.analytics("optimistic_skybus.subscription_activation_succeeded", { channel });
        return null;
      },
      (err: unknown) => {
        state.activationResult = "failed";
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error(
          `[OptimisticUnifiedSkybus] Failed to activate subscription for ${channel}`,
          err
        );
        this.analytics("optimistic_skybus.subscription_activation_failed", {
          channel,
          error: error.message,
        });
        return error;
      }
    );
  }

  /**
   * Re-attempt activation for a state whose previous handshake failed.
   * Called from the connection listener on reconnect. We deliberately do
   * NOT re-run for already-succeeded states: the LivekitSkybusProvider
   * calls `republishExistingSubscriptions` on its own to rebuild the
   * provider-level subscription, and `Subscription.subscribed` remains
   * true across a transport disconnect.
   */
  private scheduleReactivation<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>): void {
    if (state.activationResult !== "failed") return;
    this.analytics("optimistic_skybus.subscription_reactivation_scheduled", {
      channel: state.config.responseChannel.channel,
    });
    this.kickoffActivation(state);
  }

  /**
   * Invoke `inflight.matchFn` defensively. Match functions are user-provided
   * and may throw for malformed messages; a throw should not abort the
   * interceptor (which would also abort every subscriber on this channel).
   * Treat a throw as "does not match" and log it.
   */
  private safeMatch<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(inflight: PendingEntry<TRequest, TResponse>, msg: TResponse): boolean {
    try {
      return inflight.matchFn(msg, inflight.request);
    } catch (err) {
      logger.error("[OptimisticUnifiedSkybus] matchFn threw; treating as non-match", err);
      this.analytics("optimistic_skybus.user_callback_threw", {
        callback: "matchFn",
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }

  /**
   * Rebase `inflight` (optional) and every `queued` entry on top of the
   * provided base. If `cfg.rebase` throws on any step we log and fall back
   * to the last successfully rebased view so the interceptor can still
   * broadcast a consistent state to listeners rather than crashing the
   * subscription.
   */
  private rebaseOntoBase<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    state: ChannelState<TRequest, TResponse>,
    base: TResponse,
    inflight: PendingEntry<TRequest, TResponse> | null,
    queued: ReadonlyArray<PendingEntry<TRequest, TResponse>>
  ): TResponse {
    let view: TResponse = base;
    const steps: Array<PendingEntry<TRequest, TResponse>> = [];
    if (inflight) steps.push(inflight);
    for (const q of queued) steps.push(q);
    for (const step of steps) {
      try {
        view = state.config.rebase(view, step.request);
      } catch (err) {
        logger.error(
          `[OptimisticUnifiedSkybus] rebase threw on ${state.config.responseChannel.channel}; ` +
            `skipping that overlay`,
          err
        );
        this.analytics("optimistic_skybus.user_callback_threw", {
          callback: "rebase",
          channel: state.config.responseChannel.channel,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    return view;
  }

  /** Resolve the in-flight entry, stop its retry timer, and drain the queue. */
  private settleInflight<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    state: ChannelState<TRequest, TResponse>,
    entry: PendingEntry<TRequest, TResponse>,
    response: TResponse
  ) {
    this.cancelTimers(entry);
    if (state.inflight === entry) state.inflight = null;
    entry.resolve(response);
    this.drainQueue(state);
  }

  /** Reject the in-flight entry (timeout exhausted), rollback, drain queue. */
  private failInflight<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(
    state: ChannelState<TRequest, TResponse>,
    entry: PendingEntry<TRequest, TResponse>,
    err: unknown
  ) {
    this.analytics("optimistic_skybus.request_failed", {
      channel: channelKey(state.config.requestChannel, state.config.responseChannel),
      debugKey: entry.requestConfig.debugKey,
      queueLen: state.queue.length,
      hasRealLatest: state.realLatest !== undefined,
      reason: err instanceof Error ? err.message : String(err),
      ...extractAnalyticsContext(entry.request),
    });
    this.cancelTimers(entry);
    if (state.inflight === entry) state.inflight = null;

    // Apply the channel's failure policy to split the queue into survivors
    // and aborted entries. Done BEFORE the rollback view is computed so we
    // don't reapply overlays we're about to drop.
    const aborted = this.applyFailurePolicy(state, err);

    // Roll back: reset currentView to realLatest and reapply any SURVIVING
    // queued entries on top so they remain optimistically visible.
    let view: TResponse | undefined = state.realLatest;
    for (const q of state.queue) {
      try {
        view =
          view !== undefined
            ? state.config.rebase(view, q.request)
            : state.config.buildOptimisticResponse(undefined, q.request);
      } catch (rebaseErr) {
        logger.error(
          `[OptimisticUnifiedSkybus] rebase threw during rollback on ` +
            `${state.config.responseChannel.channel}; dropping that overlay`,
          rebaseErr
        );
        this.analytics("optimistic_skybus.user_callback_threw", {
          callback: "rebase_rollback",
          channel: state.config.responseChannel.channel,
          error: rebaseErr instanceof Error ? rebaseErr.message : String(rebaseErr),
        });
      }
    }
    if (view !== undefined) {
      state.currentView = view;
      this.broadcastSynthetic(state, view);
    } else {
      state.currentView = undefined;
    }

    entry.reject(err);
    // Fire error listeners defensively — a throwing listener must not abort
    // the rest of the listeners or prevent the queue from draining. The
    // helper snapshots the listener array so an unsubscribe-during-fire
    // doesn't skip a sibling listener.
    this.fireErrorListeners(state, { request: entry.request, reason: err });

    // Reject any queued entries the policy aborted. Their `request_failed`
    // is implied by the upstream `queue_aborted` event; we still fire
    // per-entry error listeners so UI layers can tear down their
    // optimistic state.
    for (const q of aborted) {
      this.cancelTimers(q);
      q.reject(err);
      this.fireErrorListeners(state, { request: q.request, reason: err });
    }

    this.drainQueue(state);
  }

  private pushOptimistic<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>, request: TRequest) {
    let synthetic: TResponse;
    try {
      synthetic = state.config.buildOptimisticResponse(state.currentView, request);
    } catch (err) {
      logger.error(
        `[OptimisticUnifiedSkybus] buildOptimisticResponse threw on ` +
          `${state.config.responseChannel.channel}; skipping optimistic push`,
        err
      );
      this.analytics("optimistic_skybus.user_callback_threw", {
        callback: "buildOptimisticResponse",
        channel: state.config.responseChannel.channel,
        error: err instanceof Error ? err.message : String(err),
      });
      return;
    }
    state.currentView = synthetic;
    this.broadcastSynthetic(state, synthetic);
  }

  private broadcastSynthetic<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>, msg: TResponse) {
    state.isPushingSynthetic = true;
    try {
      state.subscription.pushNewLatest(msg);
    } finally {
      state.isPushingSynthetic = false;
    }
  }

  /**
   * Kick off an optimistic entry against the vehicle. The class owns the
   * publish + retry/timeout machinery here (we do NOT call super.makeRequest)
   * because:
   *   1. We want detection of matching responses to ignore synthetic
   *      messages we broadcast through the subscription.
   *   2. We already track `realLatest` in the interceptor and resolve the
   *      in-flight entry there the moment a matching vehicle message lands.
   *
   * We await two gates before the first publish:
   *   1. `connectedGate` — ensures the transport is connected. On a
   *      reconnect, the gate is replaced with a fresh pending promise so
   *      requests initiated during the outage queue up until we're back
   *      online.
   *   2. `state.activation` — ensures the response-channel subscription
   *      handshake has completed. Avoids publishing into a channel the
   *      vehicle isn't listening to yet (would silently burn the full
   *      timeout). On reconnect after a failed activation, the gate is
   *      replaced with a fresh activation promise.
   */
  private startInflight<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>, entry: PendingEntry<TRequest, TResponse>) {
    state.inflight = entry;

    const timeout = entry.requestConfig.timeout ?? SUBSCRIPTION_RETRY_DEFAULT_INTERVAL;
    let retriesLeft = entry.requestConfig.retries ?? 0;

    // Snapshot the current gate at enqueue time so we wait for whichever
    // gate was pending when this entry was started.
    //
    // DEFENSIVE: re-sample `provider.isConnected()` directly. Some
    // transports (notably `LivekitTransportLayer` historically) mutated
    // their connected flag without invoking their listeners, which meant
    // the listener-driven `connectedGate` never resolved even though the
    // transport was connected. Using the live value as a fast path makes
    // the class robust to that class of bug — the gate is only awaited
    // when the provider actually reports disconnected right now.
    const gate = this.provider.isConnected() ? Promise.resolve() : this.connectedGate;

    const reqChannel = state.config.requestChannel.channel;
    const respChannel = state.config.responseChannel.channel;

    // Watchdog: if neither gate resolves within a few seconds, log loudly
    // so we can see the request is wedged waiting on a gate rather than
    // silently sitting forever. The handle is parked on `entry.watchdog`
    // so `cancelEntry` and `failInflight` can clear it without a closure
    // — otherwise a stopped entry could fire `gates_stuck` seconds after
    // it's gone.
    let connectedResolved = false;
    let activationResolved = false;
    entry.watchdog = setTimeout(() => {
      entry.watchdog = undefined;
      if (entry.stopped || state.inflight !== entry) return;
      if (!connectedResolved || !activationResolved) {
        this.analytics("optimistic_skybus.gates_stuck", {
          requestChannel: reqChannel,
          responseChannel: respChannel,
          connected: connectedResolved,
          activation: activationResolved,
          debugKey: entry.requestConfig.debugKey,
        });
      }
    }, 2000);

    gate
      .then(() => {
        connectedResolved = true;
        // Re-read `state.activation` after the gate resolves so we pick up
        // a fresh activation if reconnection scheduled one (see
        // `scheduleReactivation`).
        return state.activation;
      })
      .then(activationErr => {
        activationResolved = true;
        if (entry.watchdog) {
          clearTimeout(entry.watchdog);
          entry.watchdog = undefined;
        }
        // Bail if the entry was already settled (e.g. replaced, rolled
        // back, or stopped by SkybusPromise.stop()).
        if (entry.stopped || state.inflight !== entry) {
          return;
        }
        if (activationErr) {
          this.failInflight(state, entry, activationErr);
          return;
        }
        entry.request.setUtime(Date.now() * 1000);
        this.analytics("optimistic_skybus.request_published", {
          requestChannel: reqChannel,
          responseChannel: respChannel,
          debugKey: entry.requestConfig.debugKey,
          retriesConfigured: entry.requestConfig.retries ?? 0,
          timeoutMs: timeout,
          ...extractAnalyticsContext(entry.request),
        });
        this.publish(state.config.requestChannel, entry.request);
        entry.retryTimer = setInterval(() => {
          if (entry.stopped || state.inflight !== entry) {
            if (entry.retryTimer) clearInterval(entry.retryTimer);
            return;
          }
          if (retriesLeft <= 0) {
            this.failInflight(
              state,
              entry,
              `Optimistic request timed out on ` +
                `${state.config.requestChannel.channel} -> ` +
                `${state.config.responseChannel.channel}`
            );
            return;
          }
          retriesLeft--;
          entry.request.setUtime(Date.now() * 1000);
          this.analytics("optimistic_skybus.request_retried", {
            requestChannel: reqChannel,
            responseChannel: respChannel,
            debugKey: entry.requestConfig.debugKey,
            retriesLeft,
            ...extractAnalyticsContext(entry.request),
          });
          this.publish(state.config.requestChannel, entry.request);
        }, timeout);
      });
  }

  private drainQueue<
    TRequest extends WireMsg & { setUtime(utime: number): void },
    TResponse extends WireMsg,
  >(state: ChannelState<TRequest, TResponse>) {
    // Skip any entries the caller stopped while they were queued.
    while (state.queue.length > 0 && state.queue[0].stopped) {
      state.queue.shift();
    }
    const next = state.queue.shift();
    if (!next) {
      this.analytics("optimistic_skybus.queue_drained", {
        channel: channelKey(state.config.requestChannel, state.config.responseChannel),
      });
      return;
    }
    // Do not re-push the optimistic response here — the queued overlay is
    // already reflected in `currentView` (it was pushed when the entry was
    // enqueued, and — on a matching settle — it's reapplied via
    // `rebaseOntoBase` in the interceptor).
    this.startInflight(state, next);
  }
}
